import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import UtilizationTrends from './UtilizationTrends';
import InvoicesYOY from './InvoicesYOY';
import InvoiceStats from './InvoiceStats';
import CashflowSummary from './CashflowSummary';
import MRBalancePie from './MRBalancePie';
import MRBalanceTable from './MRBalanceTable';
import ExpiryDocuments from './ExpiryDocuments';
import EOLCondition from './EOLCondition';
import { FilterList, Refresh } from '@mui/icons-material';
import { Grid, Box, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import OverduedaysTable from './OverduedaysTable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { PageLoader } from '../../../shared_elements';
import { getExpiryOfDocumentsApi, getMrReportListApi, getOverdueDaysApi } from '../apiService';
import '../styles/statistic.scss';

const currentYear = new Date().getFullYear();

const getInitialUtilisationTrendYear = (leaseEndDate) => {
    const leaseEndYear = moment(leaseEndDate).year();
    return currentYear <= leaseEndYear ? currentYear : leaseEndYear;
};

const INVOICES_YOY_CARD_PADDING_Y = 30;

const Dashboard = ({ currency = 'USD' }) => {
    const theme = useTheme();
    const isChartsRowMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const utilizationCardRef = useRef(null);
    const [pairedChartCardsHeight, setPairedChartCardsHeight] = useState(null);

    const { leaseBasicDetails, currencies } = useSelector((state) => state.leases);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [utilizationType, setUtilizationType] = useState('fh');
    const [utilisationTrendYear, setUtilisationTrendYear] = useState(currentYear);
    const [refreshKey, setRefreshKey] = useState(0);
    const params=useParams();
    const [overDueData,setOverDueData]=useState({});
    const [mrFundList,setMrFundList]=useState({});
    const [expiryOfDocument,setExpiryOfDocument]=useState({});
    const [isDashboardLoading, setDashboardLoading] = useState(false);
    const [isUtilizationLoading, setUtilizationLoading] = useState(false);
    const isFirstRender = useRef(true);
    const skipNextUtilizationFetch = useRef(false);
    const prevCurrencyRef = useRef(currency);
    const currencyOptions = currencies?.length > 0 ? currencies : [{ id: 27, code: 'GBP' }, { id: 3, code: 'EURO' }, { id: 1, code: 'USD' }];
    const currencySymbol = currencyOptions.find((cur) => String(cur.code) === String(currency))?.symbol || '$';

    const minYear = startDate?.isValid() ? startDate.year() : currentYear;
    const maxYear = endDate?.isValid() ? endDate.year() : currentYear;

    const handleUtilYearChange = (direction) => {
        setUtilisationTrendYear(prev => direction === 'prev' ? prev - 1 : prev + 1);
    };

    // Set dates from lease when available
    useEffect(() => {
        if (leaseBasicDetails?.start_date && leaseBasicDetails?.end_date) {
            const leaseStart = moment(leaseBasicDetails.start_date);
            const leaseEnd = moment(leaseBasicDetails.end_date);
            setStartDate(leaseStart);
            setEndDate(leaseEnd);
            setUtilisationTrendYear(getInitialUtilisationTrendYear(leaseBasicDetails.end_date));
        }
    }, [leaseBasicDetails?.start_date, leaseBasicDetails?.end_date]);

    useEffect(() => {
        if (!startDate?.isValid() || !endDate?.isValid()) return;
        if (isFirstRender.current) {
            isFirstRender.current = false;
            getDashboardData();
            getMrFundData();
            getExpiryDocumentData();
            return;
        }
        skipNextUtilizationFetch.current = true;
        setUtilisationTrendYear(prev => Math.min(Math.max(prev, startDate.year()), endDate.year()));
        getDashboardData();
        getMrFundData();
        getExpiryDocumentData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (!startDate?.isValid() || !endDate?.isValid()) return;
        if (prevCurrencyRef.current === currency) return;
        prevCurrencyRef.current = currency;
        getDashboardData();
    }, [currency]);

    useEffect(() => {
        if (isFirstRender.current) return;
        if (skipNextUtilizationFetch.current) {
            skipNextUtilizationFetch.current = false;
            return;
        }
        getUtilizationData();
    }, [utilisationTrendYear, utilizationType]);

    const buildDashboardQuery = () => ({
        overdue_days: '30,60,90',
        lease_slug: params?.leaseSlug,
        start_date: startDate?.format('YYYY-MM-DD'),
        end_date: endDate?.format('YYYY-MM-DD'),
        utilization_type: utilizationType,
        utilisation_trend_year: utilisationTrendYear,
        currency,
    });

    const getDashboardData = () => {
        setDashboardLoading(true);
        globalGetService(getOverdueDaysApi(buildDashboardQuery()))
            .then(response => {
                if (checkApiStatus(response)) {
                    setOverDueData(response?.data?.data);
                }
            })
            .finally(() => setDashboardLoading(false));
    };

    const getUtilizationData = () => {
        setUtilizationLoading(true);
        globalGetService(getOverdueDaysApi(buildDashboardQuery()))
            .then(response => {
                if (checkApiStatus(response)) {
                    const data = response?.data?.data;
                    setOverDueData(prev => ({
                        ...prev,
                        utilization_trends: data?.utilization_trends,
                        utilization_summary: data?.utilization_summary,
                    }));
                }
            })
            .finally(() => setUtilizationLoading(false));
    };

    const getMrFundData = () => {
        globalGetService(getMrReportListApi(params?.leaseSlug, { start_date: startDate?.format('YYYY-MM-DD'), end_date: endDate?.format('YYYY-MM-DD') }))
            .then(response => {
                if (checkApiStatus(response)) {
                    setMrFundList(response?.data?.data);
                }
            });
    };

    const getExpiryDocumentData = (query = {}) => {
        globalGetService(getExpiryOfDocumentsApi(params?.leaseSlug, query))
            .then(response => {
                if (checkApiStatus(response)) {
                    setExpiryOfDocument(response?.data?.data);
                }
            });
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
        getDashboardData();
        getMrFundData();
        getExpiryDocumentData();
    };

    useLayoutEffect(() => {
        const el = utilizationCardRef.current;
        if (!el) return;

        const measure = () => {
            const h = el.getBoundingClientRect().height;
            if (h > 0) {
                setPairedChartCardsHeight(Math.round(h));
            }
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const yoyScrollAreaMaxHeight =
        isChartsRowMdUp && pairedChartCardsHeight != null
            ? Math.max(200, pairedChartCardsHeight - INVOICES_YOY_CARD_PADDING_Y)
            : undefined;

    return (
        <Box className="statistics-dashboard-root">
            {/* Filters and Controls */}
            <Box className="statistics-filters-row">
                <Box className="statistics-filters-left">
                    {/* <button style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 16px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s'
                    }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                        <FilterList fontSize="small" />
                        <span>Filters</span>
                    </button> */}
                    <Box className="statistics-date-pickers">
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(date) => setStartDate(date)}
                                slotProps={{
                                    textField: {
                                        InputLabelProps: { shrink: true },
                                        size: 'small',
                                        margin: 'none',
                                        helperText: '',
                                        error: false,
                                    },
                                }}
                                className="statistics-date-picker"
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(date) => setEndDate(date)}
                                slotProps={{
                                    textField: {
                                        InputLabelProps: { shrink: true },
                                        size: 'small',
                                        margin: 'none',
                                        helperText: '',
                                        error: false,
                                    },
                                }}
                                className="statistics-date-picker"
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>
                <Box className="statistics-filters-right">
                    <button
                        onClick={handleRefresh}
                        className="statistics-refresh-btn"
                    >
                        <Refresh fontSize="small" />
                        <span>Refresh</span>
                    </button>
                    {/* <Button startIcon={<ArrowDownwardIcon />} variant="outlined" size='small' className='btn-primary'> Export </Button> */}
                </Box>
            </Box>

            {/* Charts Grid */}
            <Grid container spacing={2} className="statistics-grid-row">
                {/* Utilization Trends */}
                <Grid size={{ xs: 12, sm: 12, md: 7, xl: 7 }}>
                    <Box
                        ref={utilizationCardRef}
                        className="stat-card stat-card--padded-lg"
                    >
                        <UtilizationTrends
                            utilsTrendsData={overDueData?.utilization_trends}
                            key={refreshKey}
                            utilizationType={utilizationType}
                            setUtilizationType={setUtilizationType}
                            utilization_summary={overDueData?.utilization_summary}
                            year={utilisationTrendYear}
                            canGoPrev={utilisationTrendYear > minYear}
                            canGoNext={utilisationTrendYear < maxYear}
                            handleYearChange={handleUtilYearChange}
                            isLoading={isUtilizationLoading}
                        />
                    </Box>
                </Grid>

                {/* Invoices YOY */}
                <Grid size={{ xs: 12, sm: 12, md: 5, xl: 5 }}>
                    <Box
                        className="stat-card stat-card--bg-white stat-card--padded-md stat-card--border-box"
                        style={
                            isChartsRowMdUp && pairedChartCardsHeight != null
                                ? {
                                      height: pairedChartCardsHeight,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      minHeight: 0,
                                  }
                                : undefined
                        }
                    >
                        <InvoicesYOY
                            key={refreshKey}
                            invoicesYOYData={overDueData?.invoice_amount_yoy || []}
                            currencySymbol={currencySymbol}
                            scrollAreaMaxHeight={yoyScrollAreaMaxHeight}
                        />
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} className="statistics-grid-row">

                <Grid size={{ xs: 12, sm: 12, md: 12,  lg:5, xl: 5 }}>
                    <InvoiceStats invsummary={overDueData?.summary} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg:3, xl: 4 }}>
                    <OverduedaysTable overDueDays={overDueData?.overdue_buckets} />
                </Grid>

                {/* MR Balance Pie */}
                <Grid size={{ xs: 12, sm: 12, md: 8, lg:4, xl: 3 }}>
                    <Box className="stat-card stat-card--bg-white stat-card--padded-sm">
                        <MRBalancePie key={refreshKey} mrBalanceData={overDueData?.invoice_type_weightage|| []} as_of_date={overDueData?.as_of_date} />
                    </Box>
                </Grid>
            </Grid>

            {/* Tables Grid */}
            <Grid container spacing={2} className="statistics-grid-row">

                {/* Cashflow Summary */}
                <Grid size={{ xs: 12, sm: 12, md: 7, xl: 8 }}>
                    <Box className="stat-card stat-card--bg-white stat-card--padded-lg">
                        <CashflowSummary key={refreshKey} cashflowSummaryYoy={overDueData?.cashflow_summary_yoy} />
                    </Box>
                </Grid>

                {/* MR Balance Table */}
                <Grid size={{ xs: 12, sm: 12, md: 5, xl: 4 }}>
                    <Box className="stat-card stat-card--bg-white stat-card--padded-lg">
                        <MRBalanceTable mrFundList={mrFundList?.list} endDate={endDate} startDate={startDate} leaseSlug={params?.leaseSlug} as_of_date={overDueData?.as_of_date} />
                    </Box>
                </Grid>
            </Grid>
            <Grid container spacing={2} className="statistics-grid-row">


                {/* Expiry Documents */}
                <Grid size={{ xs: 12, sm: 12, md: 7, xl: 6 }}>
                    <Box className="stat-card stat-card--bg-white stat-card--padded-sm">
                        <ExpiryDocuments
                            expiryData={expiryOfDocument?.list}
                            pagination={expiryOfDocument?.pagination}
                            leaseSlug={params?.leaseSlug}
                            onPaginationChange={getExpiryDocumentData}
                        />
                    </Box>
                </Grid>
                {/* EOL Condition Table */}
                {/* <Grid size={{ xs: 12, sm: 12, md: 6, xl: 6 }}>
                    <Box style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                        padding: '20px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <EOLCondition />
                    </Box>
                </Grid> */}
            </Grid>
            {isDashboardLoading ? <PageLoader /> : null}
        </Box>
    );
};

export default Dashboard;